/**
 * Models for the Defect Overview feature.
 * Migrated from web-app-legacy/js/app/Components/Defects/Overview/
 */

import { IEmployee, ISeverityLevel, IDefectState, ICategory, ICategory2, ICategory3 } from '../defect.models';

// Sort options enum.
// Values are the protobuf enum names (SortOptionProto in SkomapEntity.proto) and
// are sent to the API as strings, same as the legacy app. Numeric values must not
// be used here: they do not line up with the proto ordinals.
export enum SortOption {
  Severity = 'Severity',
  State = 'State',
  CreatedAt = 'CreatedAt',
}

// Sort order enum. Same string-name contract as SortOption (SortOrderProto).
export enum SortOrder {
  Asc = 'Asc',
  Desc = 'Desc',
}

// Sort info structure
export interface DefectSortInfo {
  sortOption: SortOption;
  sortOrder: SortOrder;
}

// Default overview order: newest defects first. Without an explicit sort the
// API falls back to severity, which building managers did not want.
export const DEFAULT_SORT: DefectSortInfo = {
  sortOption: SortOption.CreatedAt,
  sortOrder: SortOrder.Desc,
};

// Internal filter state (used by component)
export interface DefectFilters {
  siteId: number;
  search: string | null;
  severityLevelId: number | null;
  defectStateId: number | null;
  category1Id: number | null;
  category2Id: number | null;
  category3Id: number | null;
  createdAtFrom: number | null;
  createdAtTo: number | null;
  managerPersonalNum: string | null;
  coordinatorPersonalNum: string | null;
  sortInfo: DefectSortInfo[];
}

// Nullable wrapper for API filter values
export interface NullableValue<T> {
  hasValue: boolean;
  value: T;
}

// DTO format for API (matches backend expectations)
export interface IDefectFiltersDto {
  siteId: number;
  search: string | null;
  severityLevelId: NullableValue<number>;
  defectStateId: NullableValue<number>;
  category1Id: NullableValue<number>;
  category2Id: NullableValue<number>;
  category3Id: NullableValue<number>;
  createdAtFrom: NullableValue<number>;
  createdAtTo: NullableValue<number>;
  managerPersonalNum: string | null;
  coordinatorPersonalNum: string | null;
  sortInfoes: DefectSortInfo[];
}

// View model for defect list item
export interface DefectItemViewModel {
  id: number;
  title: string;
  description: string;
  faultState: string | null;
  stateId: number;
  severity: string | null;
  severityLevelId: number;
  category1: string | null;
  category2: string | null;
  category3: string | null;
  createdBy: string;
  createdAt: Date;
  statusIcon: string;
  statusIconDark: string;
  managers: IEmployee[];
  coordinator: IEmployee | null;
  managersNames: string;
  isArchived: boolean;
  editable: boolean;
}

// Sort dropdown option
export interface SortDropdownOption {
  id: SortOption;
  title: string;
  type: SortOrder;
}

// Filters view model (dropdown options)
export interface FiltersViewModel {
  severities: ISeverityLevel[];
  states: IDefectState[];
  categories1: ICategory[];
  categories2: ICategory2[];
  categories3: ICategory3[];
  managers: IEmployee[];
  coordinators: IEmployee[];
}

// User roles are defined once in settings.models.ts and re-exported here so the
// existing imports keep resolving. There used to be a second copy in this file
// whose Administrator value was 'Administrator'; the API sends 'Admin', so every
// role check for administrators silently failed.
export { UserRole } from '../../settings/settings.models';

// Helper to create default filters
export function createDefaultFilters(siteId: number): DefectFilters {
  return {
    siteId,
    search: null,
    severityLevelId: null,
    defectStateId: null,
    category1Id: null,
    category2Id: null,
    category3Id: null,
    createdAtFrom: null,
    createdAtTo: null,
    managerPersonalNum: null,
    coordinatorPersonalNum: null,
    sortInfo: [{ ...DEFAULT_SORT }],
  };
}

// Helper to convert filters to DTO format
export function filtersToDto(filters: DefectFilters): IDefectFiltersDto {
  return {
    siteId: filters.siteId,
    search: filters.search,
    severityLevelId: {
      hasValue: filters.severityLevelId !== null,
      value: filters.severityLevelId ?? 0,
    },
    defectStateId: {
      hasValue: filters.defectStateId !== null,
      value: filters.defectStateId ?? 0,
    },
    category1Id: {
      hasValue: filters.category1Id !== null,
      value: filters.category1Id ?? 0,
    },
    category2Id: {
      hasValue: filters.category2Id !== null,
      value: filters.category2Id ?? 0,
    },
    category3Id: {
      hasValue: filters.category3Id !== null,
      value: filters.category3Id ?? 0,
    },
    createdAtFrom: {
      hasValue: filters.createdAtFrom !== null,
      value: filters.createdAtFrom ?? 0,
    },
    createdAtTo: {
      hasValue: filters.createdAtTo !== null,
      value: filters.createdAtTo ?? 0,
    },
    managerPersonalNum: filters.managerPersonalNum,
    coordinatorPersonalNum: filters.coordinatorPersonalNum,
    sortInfoes: filters.sortInfo,
  };
}

// Helper to check if any filter is active
export function hasActiveFilters(filters: DefectFilters): boolean {
  return (
    filters.search !== null ||
    filters.severityLevelId !== null ||
    filters.defectStateId !== null ||
    filters.category1Id !== null ||
    filters.category2Id !== null ||
    filters.category3Id !== null ||
    filters.createdAtFrom !== null ||
    filters.createdAtTo !== null ||
    filters.managerPersonalNum !== null ||
    filters.coordinatorPersonalNum !== null
  );
}

// Helper to format manager names
export function formatManagerNames(managers: IEmployee[]): string {
  if (!managers || managers.length === 0) {
    return '';
  }
  return managers.map(m => m.fullName).join(', ');
}

// Helper to format manager phones
export function formatManagerPhones(managers: IEmployee[]): string {
  if (!managers || managers.length === 0) {
    return '';
  }
  return managers.map(m => m.phone).filter(p => p).join(', ');
}

// Helper to format manager emails
export function formatManagerEmails(managers: IEmployee[]): string {
  if (!managers || managers.length === 0) {
    return '';
  }
  return managers.map(m => m.email).filter(e => e).join(', ');
}
